import React, { useState, useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import ActionToolbar from '../../components/ui/ActionToolbar';
import DialogHeader from '../../components/ui/DialogHeader';
import PageHeader from '../../components/ui/PageHeader';
import { ProductoService } from '../../services/ProductoService';
import { CategoriaService } from '../../services/CategoriaService';
import { useAuth } from '../../context/AuthContext';

import { environment } from '../util/baseUrl';

const Productos = () => {
    const { user } = useAuth();
    let emptyProduct = {
        id: null,
        nombre: '',
        marca: '',
        descripcion: '',
        imagen: null,
        precioVenta: 0,
        precioCompra: 0,
        tipo: 'Único',
        idCategoria: null,
        idEmpresa: user?.idEmpresa || 1,
        variantes: []
    };

    const [tieneVariantes, setTieneVariantes] = useState(false);
    const [variantInput, setVariantInput] = useState({ valor: '', codigoBarras: '', precioVenta: null, imagen: null });
    const [codigoBarrasSimple, setCodigoBarrasSimple] = useState('');
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [productDialog, setProductDialog] = useState(false);
    const [deleteProductDialog, setDeleteProductDialog] = useState(false);
    const [product, setProduct] = useState(emptyProduct);
    const [selectedProducts, setSelectedProducts] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [uploadingImage, setUploadingImage] = useState(false);
    const toast = useRef(null);
    const dt = useRef(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const dataProd = await ProductoService.getAll();
            const dataCat = await CategoriaService.getAll();
            setProducts(dataProd.data || []);
            setCategories(dataCat.data || []);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los datos', life: 3000 });
        }
    };

    const openNew = () => {
        setProduct(emptyProduct);
        setTieneVariantes(false);
        setCodigoBarrasSimple('');
        setVariantInput({ valor: '', codigoBarras: '', precioVenta: null, imagen: null });
        setSubmitted(false);
        setProductDialog(true);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setProductDialog(false);
    };

    const hideDeleteProductDialog = () => {
        setDeleteProductDialog(false);
    };

    // ========== Image Upload ==========
    const handleUploadImage = async (file, identifier) => {
        setUploadingImage(true);
        try {
            const result = await ProductoService.uploadImage(file, identifier);
            if (result?.data?.url) {
                return result.data.url;
            }
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se obtuvo URL de imagen', life: 3000 });
            return null;
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al subir imagen', life: 3000 });
            return null;
        } finally {
            setUploadingImage(false);
        }
    };

    const removeMainImage = () => {
        if (product.imagen) {
            ProductoService.deleteImage(product.imagen).catch(() => {});
        }
        setProduct(prev => ({ ...prev, imagen: null }));
    };

    // ========== CRUD ==========
    const saveProduct = async () => {
        setSubmitted(true);
        if (!product.nombre.trim() || !product.idCategoria) return;

        let payload = { ...product };
        payload.idEmpresa = payload.idEmpresa || user?.idEmpresa || 1;

        if (tieneVariantes) {
            if (payload.variantes.length === 0) {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Debe agregar al menos una variante.', life: 3000 });
                return;
            }
        } else {
            payload.tipo = 'Único';
            payload.variantes = [{
                valor: 'Único',
                codigoBarras: codigoBarrasSimple || ''
            }];
        }

        try {
            if (payload.id) {
                const response = await ProductoService.update(payload.id, payload);
                if (!response?.success) {
                    throw new Error(response?.message || 'No se pudo actualizar el producto');
                }
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Producto Actualizado', life: 3000 });
            } else {
                const response = await ProductoService.create(payload);
                if (!response?.success) {
                    throw new Error(response?.message || 'No se pudo crear el producto');
                }
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Producto Creado', life: 3000 });
            }
            setProductDialog(false);
            setProduct(emptyProduct);
            loadData();
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'No se pudo guardar el producto';
            toast.current.show({ severity: 'error', summary: 'Error', detail: message, life: 3000 });
        }
    };

    const editProduct = (prod) => {
        const hasMultipleVariants = prod.variantes && prod.variantes.length > 0 &&
            !(prod.variantes.length === 1 && prod.variantes[0].valor === 'Único');
        setTieneVariantes(hasMultipleVariants);
        if (!hasMultipleVariants && prod.variantes && prod.variantes.length === 1) {
            setCodigoBarrasSimple(prod.variantes[0].codigoBarras || '');
        } else {
            setCodigoBarrasSimple('');
        }
        setProduct({ ...prod });
        setProductDialog(true);
    };

    const confirmDeleteProduct = (prod) => {
        setProduct(prod);
        setDeleteProductDialog(true);
    };

    const deleteProduct = async () => {
        try {
            await ProductoService.delete(product.id);
            setDeleteProductDialog(false);
            setProduct(emptyProduct);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Producto Eliminado', life: 3000 });
            loadData();
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el producto', life: 3000 });
        }
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || '';
        setProduct(prev => ({ ...prev, [name]: val }));
    };

    const onInputNumberChange = (e, name) => {
        const val = e.value || 0;
        setProduct(prev => ({ ...prev, [name]: val }));
    };

    const addVariant = () => {
        if (variantInput.valor.trim()) {
            setProduct(prev => ({ ...prev, variantes: [...prev.variantes, { ...variantInput }] }));
            setVariantInput({ valor: '', codigoBarras: '', precioVenta: null, imagen: null });
        } else {
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Escriba un valor para la variante', life: 3000 });
        }
    };

    const removeVariant = (index) => {
        const removed = product.variantes[index];
        if (removed?.imagen) {
            ProductoService.deleteImage(removed.imagen).catch(() => {});
        }
        setProduct(prev => ({ ...prev, variantes: prev.variantes.filter((_, i) => i !== index) }));
    };

    // ========== Templates de Tabla ==========
    const categoryBodyTemplate = (rowData) => {
        const cat = categories.find(c => c.id === rowData.idCategoria);
        return cat ? cat.nombre : 'Sin Categoría';
    };

    const priceBodyTemplate = (rowData) => {
        return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(rowData.precioVenta || 0);
    };

    const stockBodyTemplate = (rowData) => {
        const stockActual = rowData.stock || 0;
        const stockMin = rowData.stockMinimo || 0;
        const className = classNames('font-bold', {
            'text-green-500': stockActual > stockMin,
            'text-red-500': stockActual <= stockMin && stockActual > 0,
            'text-orange-500': stockActual === 0
        });
        return <span className={className}>{stockActual}</span>;
    };

    const imageBodyTemplate = (rowData) => {
        const src = rowData.imagen ? `${environment.backendUrl}${rowData.imagen}` : 'https://primefaces.org/cdn/primereact/images/product/bamboo-watch.jpg';
        return <img src={src} alt={rowData.nombre} className="shadow-2 border-round" style={{ width: '64px', height: '64px', objectFit: 'cover' }} />;
    };

    const actionBodyTemplate = (rowData) => (
        <React.Fragment>
            <Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => editProduct(rowData)} />
            <Button icon="pi pi-trash" rounded outlined onClick={() => confirmDeleteProduct(rowData)} />
        </React.Fragment>
    );

    const varianteCountBody = (rowData) => {
        const count = rowData.variantes ? rowData.variantes.length : 0;
        if (count <= 1) return <span className="text-500">—</span>;
        return <span className="font-bold text-primary">{count} variantes</span>;
    };

    const formatCurrency = (val) => {
        if (val == null) return '';
        return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(val);
    };

    const resolveImageUrl = (url) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${environment.backendUrl}${url}`;
    };

    // ========== Componente de Upload Reutilizable ==========
    const ImageUploadBox = ({ imageUrl, onUpload, onRemove, label, loading }) => {
        const fileRef = useRef(null);

        if (imageUrl) {
            return (
                <div className="relative inline-block">
                    <img
                        src={resolveImageUrl(imageUrl)}
                        alt={label}
                        className="shadow-2 border-round"
                        style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                    />
                    <Button
                        icon="pi pi-times"
                        rounded
                        text
                        severity="danger"
                        style={{ position: 'absolute', top: '-8px', right: '-8px', width: '1.8rem', height: '1.8rem' }}
                        onClick={onRemove}
                    />
                </div>
            );
        }

        return (
            <div
                className="flex flex-column align-items-center justify-content-center border-2 border-dashed border-300 border-round p-3 cursor-pointer hover:border-primary transition-colors transition-duration-200"
                style={{ width: '120px', height: '120px' }}
                onClick={() => fileRef.current?.click()}
            >
                {loading ? (
                    <i className="pi pi-spin pi-spinner text-2xl text-500" />
                ) : (
                    <>
                        <i className="pi pi-camera text-2xl text-500 mb-1" />
                        <span className="text-xs text-500 text-center">{label}</span>
                    </>
                )}
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) await onUpload(file);
                        e.target.value = '';
                    }}
                />
            </div>
        );
    };

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <PageHeader 
                    title="Productos" 
                    icon="pi pi-box" 
                    description="Administra los productos de tu empresa y sus variantes."
                />
                <div className="card shadow-2 border-round-xl">
                    <Toast ref={toast} />
                    <ActionToolbar
                        onNew={openNew}
                        newLabel="Nuevo Producto"
                        onSearch={(val) => setGlobalFilter(val)}
                        searchValue={globalFilter}
                        searchPlaceholder="Buscar producto..."
                        extraActions={null}
                    />

                    <DataTable ref={dt} value={products} selection={selectedProducts} onSelectionChange={(e) => setSelectedProducts(e.value)}
                        dataKey="id" paginator rows={10} rowsPerPageOptions={[5, 10, 25]} className="datatable-responsive p-datatable-sm"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} productos"
                        globalFilter={globalFilter} emptyMessage="No se encontraron productos." responsiveLayout="scroll">
                        <Column selectionMode="multiple" headerStyle={{ width: '4rem' }}></Column>
                        <Column header="Imagen" body={imageBodyTemplate}></Column>
                        <Column field="nombre" header="Nombre" sortable headerStyle={{ minWidth: '15rem' }}></Column>
                        <Column header="Variantes" body={varianteCountBody} headerStyle={{ minWidth: '8rem' }}></Column>
                        <Column field="precioVenta" header="Precio" body={priceBodyTemplate} sortable headerStyle={{ minWidth: '8rem' }}></Column>
                        <Column field="idCategoria" header="Categoría" body={categoryBodyTemplate} sortable headerStyle={{ minWidth: '10rem' }}></Column>
                        <Column field="stock" header="Stock" body={stockBodyTemplate} sortable headerStyle={{ minWidth: '8rem' }}></Column>
                        <Column body={actionBodyTemplate} headerStyle={{ minWidth: '10rem' }}></Column>
                    </DataTable>

                    {/* ========== DIALOG PRINCIPAL ========== */}
                    <Dialog
                        visible={productDialog}
                        style={{ width: '800px', maxWidth: '95vw' }}
                        header={
                            <DialogHeader
                                title={product.id ? 'Editar Producto' : 'Nuevo Producto'}
                                subtitle={product.id ? 'Modificar datos del producto' : 'Registrar un nuevo producto en el catálogo'}
                                icon="pi pi-box"
                            />
                        }
                        modal
                        className="p-fluid custom-profile-dialog"
                        footer={
                            <div className="dialog-footer-buttons">
                                <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDialog} />
                                <Button label="Guardar" icon="pi pi-check" onClick={saveProduct} autoFocus loading={uploadingImage} />
                            </div>
                        }
                        onHide={hideDialog}
                    >
                        <div className="formgrid grid mt-4 dialog-content-specific">
                            {/* --- Imagen Principal + Info General --- */}
                            <div className="field col-12 flex gap-4 align-items-start">
                                <ImageUploadBox
                                    imageUrl={product.imagen}
                                    onUpload={async (file) => {
                                        const url = await handleUploadImage(file, 'master');
                                        if (url) setProduct(prev => ({ ...prev, imagen: url }));
                                    }}
                                    onRemove={removeMainImage}
                                    label="Imagen Producto"
                                    loading={uploadingImage}
                                />
                                <div className="flex-1">
                                    <div className="formgrid grid">
                                        <div className="field col-12 md:col-6">
                                            <label htmlFor="nombre">Nombre del Producto</label>
                                            <InputText
                                                id="nombre"
                                                value={product.nombre}
                                                onChange={(e) => onInputChange(e, 'nombre')}
                                                required
                                                autoFocus
                                                placeholder="Ej: Paraguas, Memoria RAM"
                                                className={classNames({ 'p-invalid': submitted && !product.nombre })}
                                            />
                                            {submitted && !product.nombre && <small className="p-error">El nombre es requerido.</small>}
                                        </div>
                                        <div className="field col-12 md:col-6">
                                            <label htmlFor="marca">Marca</label>
                                            <InputText id="marca" value={product.marca || ''} onChange={(e) => onInputChange(e, 'marca')} placeholder="Ej: Kingston, Sony" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="field col-12 md:col-6">
                                <label htmlFor="idCategoria">Categoría</label>
                                <Dropdown
                                    id="idCategoria"
                                    value={product.idCategoria}
                                    onChange={(e) => onInputChange(e, 'idCategoria')}
                                    options={categories}
                                    optionLabel="nombre"
                                    optionValue="id"
                                    placeholder="Seleccione Categoría"
                                    className={classNames({ 'p-invalid': submitted && !product.idCategoria })}
                                />
                                {submitted && !product.idCategoria && <small className="p-error">La categoría es requerida.</small>}
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="descripcion">Descripción</label>
                                <InputTextarea id="descripcion" value={product.descripcion} onChange={(e) => onInputChange(e, 'descripcion')} rows={2} autoResize placeholder="Descripción breve" />
                            </div>

                            {/* --- Precios Generales --- */}
                            <div className="field col-12 md:col-6">
                                <label htmlFor="precioCompra">Precio de Costo</label>
                                <InputNumber id="precioCompra" value={product.precioCompra} onValueChange={(e) => onInputNumberChange(e, 'precioCompra')} mode="currency" currency="PEN" locale="es-PE" />
                            </div>
                            <div className="field col-12 md:col-6">
                                <label htmlFor="precioVenta">Precio de Venta</label>
                                <InputNumber id="precioVenta" value={product.precioVenta} onValueChange={(e) => onInputNumberChange(e, 'precioVenta')} mode="currency" currency="PEN" locale="es-PE" />
                            </div>

                            {/* --- Código de barras simple (sin variantes) --- */}
                            {!tieneVariantes && (
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="codigoBarrasSimple">Código de Barras</label>
                                    <InputText id="codigoBarrasSimple" value={codigoBarrasSimple} onChange={(e) => setCodigoBarrasSimple(e.target.value)} placeholder="Escanear o ingresar código" />
                                </div>
                            )}

                            {/* --- Toggle de Variantes --- */}
                            <div className="col-12">
                                <Divider />
                                <div className="flex align-items-center gap-3 mb-3">
                                    <Checkbox
                                        inputId="tieneVariantes"
                                        checked={tieneVariantes}
                                        onChange={(e) => {
                                            setTieneVariantes(e.checked);
                                            if (!e.checked) {
                                                setProduct(prev => ({ ...prev, tipo: 'Único', variantes: [] }));
                                            }
                                        }}
                                    />
                                    <label htmlFor="tieneVariantes" className="font-bold text-lg cursor-pointer">
                                        Este producto tiene variantes
                                    </label>
                                    <small className="text-500">(Ej: diferentes colores, tallas, capacidades)</small>
                                </div>
                            </div>

                            {/* --- Sección de Variantes --- */}
                            {tieneVariantes && (
                                <div className="col-12">
                                    <div className="surface-100 border-round-lg p-3">
                                        <div className="formgrid grid mb-3">
                                            <div className="field col-12 md:col-4 mb-0">
                                                <label className="font-semibold">Propiedad de Variante</label>
                                                <InputText
                                                    value={product.tipo === 'Único' ? '' : product.tipo}
                                                    onChange={(e) => setProduct(prev => ({ ...prev, tipo: e.target.value }))}
                                                    placeholder="Ej: Color, Talla, Capacidad"
                                                    className={classNames({ 'p-invalid': submitted && tieneVariantes && (!product.tipo || product.tipo === 'Único') })}
                                                />
                                            </div>
                                        </div>

                                        <label className="font-semibold block mb-2">Agregar Variante</label>
                                        <div className="flex gap-2 mb-3 align-items-end flex-wrap">
                                            <ImageUploadBox
                                                imageUrl={variantInput.imagen}
                                                onUpload={async (file) => {
                                                    const url = await handleUploadImage(file, 'variant');
                                                    if (url) setVariantInput(prev => ({ ...prev, imagen: url }));
                                                }}
                                                onRemove={() => {
                                                    if (variantInput.imagen) ProductoService.deleteImage(variantInput.imagen).catch(() => {});
                                                    setVariantInput(prev => ({ ...prev, imagen: null }));
                                                }}
                                                label="Imagen"
                                                loading={uploadingImage}
                                            />
                                            <div className="flex-1">
                                                <div className="formgrid grid">
                                                    <div className="field col-12 md:col-4 mb-1">
                                                        <InputText placeholder="Valor (Ej: Rojo, 32GB)" value={variantInput.valor} onChange={(e) => setVariantInput(prev => ({ ...prev, valor: e.target.value }))} />
                                                    </div>
                                                    <div className="field col-12 md:col-4 mb-1">
                                                        <InputText placeholder="Cód. de Barras" value={variantInput.codigoBarras} onChange={(e) => setVariantInput(prev => ({ ...prev, codigoBarras: e.target.value }))} />
                                                    </div>
                                                    <div className="field col-12 md:col-4 mb-1">
                                                        <InputNumber placeholder="Precio diferenciado (opc.)" value={variantInput.precioVenta} onValueChange={(e) => setVariantInput(prev => ({ ...prev, precioVenta: e.value }))} mode="currency" currency="PEN" locale="es-PE" />
                                                    </div>
                                                </div>
                                            </div>
                                            <Button type="button" label="Agregar" icon="pi pi-plus" onClick={addVariant} className="flex-shrink-0" />
                                        </div>

                                        <DataTable value={product.variantes} emptyMessage="No hay variantes registradas aún" size="small" className="border-round">
                                            <Column header="Img" body={(row) => row.imagen ?
                                                <img src={resolveImageUrl(row.imagen)} alt={row.valor} className="border-round" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                                : <i className="pi pi-image text-400" style={{ fontSize: '1.5rem' }} />
                                            } style={{ width: '60px' }}></Column>
                                            <Column field="valor" header="Valor"></Column>
                                            <Column field="codigoBarras" header="Cód. Barras" body={(row) => row.codigoBarras || '—'}></Column>
                                            <Column header="Precio" body={(row) => row.precioVenta ? formatCurrency(row.precioVenta) : <span className="text-500 font-italic">Precio general</span>}></Column>
                                            <Column body={(rowData, options) => (
                                                <Button type="button" icon="pi pi-trash" rounded text severity="danger" style={{ width: '2rem', height: '2rem' }} onClick={() => removeVariant(options.rowIndex)} />
                                            )} style={{ width: '4rem' }}></Column>
                                        </DataTable>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Dialog>

                    <Dialog visible={deleteProductDialog} style={{ width: '450px' }} header="Confirmar" modal footer={
                        <React.Fragment>
                            <Button label="No" icon="pi pi-times" outlined onClick={hideDeleteProductDialog} />
                            <Button label="Sí, Eliminar" icon="pi pi-check" onClick={deleteProduct} />
                        </React.Fragment>
                    } onHide={hideDeleteProductDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {product && <span>¿Está seguro que desea eliminar <b>{product.nombre}</b>?</span>}
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Productos;
